/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import {
  array as arrayPropType,
  bool,
  object as objectPropType,
} from "prop-types";
import EditCard from "../EditCard";
import {
  EDIT_PROFILE,
  GET_STATES,
  PROFILE_LOADING,
  STOP_PROFILE_LOADING,
} from "../../../../../redux/actionTypes";
import httpRequest from "../../../../../ajax";
import {
  ternaryResolver,
  isValidE164PhoneNumber,
  uploadFile,
} from "../../../../../utils/helpers";
import { getStateAction } from "../../../../../redux/actions/stateActions";
import { updateProfileAction } from "../../../../../redux/actions/profileActions";
import SearchLocationInput from "../../../../../components/LocationComponent";
import cogoToast from "cogo-toast";
import parseResume from "../../../../../utils/resumeParser";
import { parseResumeWithAI } from "../../../../../utils/aiResumeParser";
import ResumePreviewModal from "../Resume/ResumePreviewModal";
import "./style.css";

const useStyles = makeStyles((theme) => ({
  inputField: {
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input": {
      backgroundColor: "rgba(58, 110, 212, 0.08)", // A light, transparent blue
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiSelect-select": {
      backgroundColor: "rgba(58, 110, 212, 0.08)", // A light, transparent blue for Select
    },
  },
  // New styles for responsiveness
  locationChangeContainer: {
    display: "flex",
    justifyContent: "flex-end", // Aligns to right without fixed margins
    width: "100%",
    marginBottom: "4px",
  },
  locationLink: {
    cursor: "pointer",
    fontSize: "14px",
    color: "#3a6ed4",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  phoneHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
    flexWrap: "wrap",
    gap: "10px", // Adds space between items
    [theme.breakpoints.down("xs")]: {
      justifyContent: "space-between", // Better spacing on mobile
    },
  },
  otpInput: {
    padding: "14px 16px",
    borderRadius: "12px",
    textAlign: "center",
    fontSize: "18px",
    letterSpacing: "8px",
    [theme.breakpoints.down("xs")]: {
      padding: "12px 8px",
      fontSize: "16px",
      letterSpacing: "4px", // Reduce spacing on small screens to prevent overflow
    },
  },
}));

const BasicInfo = ({
  // industries,
  profileData,
  states,
  countries,
  profileLoading,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  // ── Feature flag — set false to allow country changes without restriction ──

  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  
  const [isOnEditLocation, setIsOnEditLocation] = useState(false);

  // Phone validation state
  const [phoneError, setPhoneError] = useState("");
  // Phone verification states
  // const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // Resume parsing states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const parseResumeInputRef = React.useRef(null);

  // Country change confirmation state
  const [countryChangeDialog, setCountryChangeDialog] = useState({
    open: false,
    pendingValue: null,
  });

  // Phone verification requirement modal state
  const [verificationRequiredDialog, setVerificationRequiredDialog] = useState({
    open: false,
    pendingCountryValue: null,
  });

  // Country code mismatch modal state
  const [countryCodeMismatchDialog, setCountryCodeMismatchDialog] = useState({
    open: false,
    targetCountry: null,
    targetCountryName: '',
    targetPhoneCode: '',
  });

  // Determine if the current phone matches the saved phone in storage
  const getStoredUserData = () => {
    try {
      return (
        JSON.parse(sessionStorage.getItem("userData")) ||
        JSON.parse(localStorage.getItem("userData")) ||
        {}
      );
    } catch (e) {
      return {};
    }
  };
  const storedUserData = getStoredUserData();
  const isPhoneSavedAndMatching =
    !!storedUserData.phone && storedUserData.phone === profileData.phone;

  const getWalletCurrencyForCountry = (countryId) =>
    String(countryId) === "160" ? "NGN" : "USD";

  const applyCountryChange = (newCountryValue) => {
    dispatch({ type: GET_STATES, payload: [] });
    const selectedCountry = countries.find(({ id }) => newCountryValue === id);
    dispatch({ type: EDIT_PROFILE, payload: { name: "country", value: newCountryValue } });
    if (selectedCountry) {
      getStateAction(selectedCountry.id, dispatch);
    }
  };

  const handleConfirmCountryChange = () => {
    if (countryChangeDialog.pendingValue !== null) {
      applyCountryChange(countryChangeDialog.pendingValue);
    }
    setCountryChangeDialog({ open: false, pendingValue: null });
  };

  const handleCancelCountryChange = () => {
    setCountryChangeDialog({ open: false, pendingValue: null });
  };

const REQUIRE_PHONE_VERIFICATION_FOR_COUNTRY_CHANGE = true;

const handleChange = ({ target: { name, value } }) => {
  if (name === "country") {
    if (profileData.country && value !== profileData.country) {
      if (REQUIRE_PHONE_VERIFICATION_FOR_COUNTRY_CHANGE) {
        const isPhoneVerified =
          profileData.is_phone_verified === 1 ||
          profileData.is_phone_verified === "1";

        if (!isPhoneVerified) {
          setVerificationRequiredDialog({
            open: true,
            pendingCountryValue: value,
          });
          return;
        }

        const targetCountry = countries.find((c) => c.id === value);
        if (targetCountry && profileData.phone) {
          const cleanPhone = profileData.phone.replace(/[\s\-\(\)]/g, "");
          const expectedPrefix = "+" + targetCountry.phonecode;
          if (!cleanPhone.startsWith(expectedPrefix)) {
            setCountryCodeMismatchDialog({
              open: true,
              targetCountry: value,
              targetCountryName: targetCountry.name,
              targetPhoneCode: targetCountry.phonecode,
            });
            return;
          }
        }
      }

      const oldCurrency = getWalletCurrencyForCountry(profileData.country);
      const newCurrency = getWalletCurrencyForCountry(value);

      if (oldCurrency !== newCurrency) {
        setCountryChangeDialog({ open: true, pendingValue: value });
        return;
      }
    }

    applyCountryChange(value);
  } else if (name === "phone") {
    if (value && !isValidE164PhoneNumber(value)) {
      setPhoneError("Phone number must be in E.164 format (e.g., +1234567890)");
    } else {
      setPhoneError("");
    }
    dispatch({ type: EDIT_PROFILE, payload: { name, value } });
    dispatch({
      type: EDIT_PROFILE,
      payload: { name: "is_phone_verified", value: 0 },
    });
  } else {
    dispatch({ type: EDIT_PROFILE, payload: { name, value } });
  }
};
  const handleLocationChange = (place) => {
    dispatch({
      type: EDIT_PROFILE,
      payload: { name: "location", value: place },
    });
  };

  // Send OTP for phone verification
  const handleSendOtp = async () => {
    // Require that the phone is saved first (user must click Update)
    if (!isPhoneSavedAndMatching) {
      cogoToast.error(
        "Please click Update to save your phone number before verifying",
        {
          hideAfter: 5,
          position: "top-center",
        }
      );
      return;
    }

    if (!profileData.phone || !profileData.country) {
      cogoToast.error("Please enter both phone number and country", {
        hideAfter: 4,
        position: "top-center",
      });
      return;
    }

    // Validate E.164 format
    if (!isValidE164PhoneNumber(profileData.phone)) {
      cogoToast.error(
        "Please enter a valid phone number in E.164 format (e.g., +1234567890)",
        {
          hideAfter: 4,
          position: "top-center",
        }
      );
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await httpRequest(
        "post",
        "/settings/profile/send-phone-verification-otp",
        {
          phone: profileData.phone,
          country: profileData.country,
        }
      );

      if (response.data.status) {
        setOtpModalOpen(true);
      }
    } catch (error) {
      cogoToast.error(error?.response?.data?.message || "Failed to send OTP", {
        hideAfter: 4,
        position: "top-center",
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      cogoToast.error("Please enter a valid 6-digit OTP", {
        hideAfter: 4,
        position: "top-center",
      });
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await httpRequest(
        "post",
        "/settings/profile/verify-phone-otp",
        {
          phone: profileData.phone,
          country: profileData.country,
          otp: otp,
        }
      );
      // response received
      if (response.data.status) {
        // Update Redux profile state so UI reflects verification immediately
        dispatch({
          type: EDIT_PROFILE,
          payload: { name: "is_phone_verified", value: 1 },
        });

        // Also persist update to session/local storage so other parts of the app read the updated value
        try {
          const stored = JSON.parse(
            sessionStorage.getItem("userData") ||
              localStorage.getItem("userData") ||
              "{}"
          );
          if (stored && typeof stored === "object") {
            stored.is_phone_verified = 1;
            // some backends may use phone_verified as a key; keep both in sync
            stored.phone_verified = 1;
            try {
              sessionStorage.setItem("userData", JSON.stringify(stored));
            } catch (e) {
              // ignore session storage write errors
            }
            try {
              localStorage.setItem("userData", JSON.stringify(stored));
            } catch (e) {
              // ignore local storage write errors
            }
          }
        } catch (e) {
          // ignore JSON parse errors
        }

        setOtpModalOpen(false);
        setOtp("");
        // Toast is shown automatically by axios interceptor
      }
    } catch (error) {
      cogoToast.error(
        error?.response?.data?.message || "Failed to verify OTP",
        {
          hideAfter: 4,
          position: "top-center",
        }
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Handle resume file upload and parsing
  const handleParseResumeClick = () => {
    parseResumeInputRef.current.click();
  };

  const handleResumeFileParse = async ({ target: { files } }) => {
    if (files && files[0]) {
      const file = files[0];

      // Validate file type
      const validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!validTypes.includes(file.type)) {
        cogoToast.error("Please upload a PDF or DOCX file", {
          hideAfter: 4,
          position: "top-center",
        });
        return;
      }

      setIsParsing(true);
      setPreviewOpen(true);

      // Upload the resume file to Cloudinary
      try {
        const uploadResult = await uploadFile(
          file,
          "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload"
        );

        if (uploadResult.success) {
          // Store the uploaded resume URL and name in session storage
          sessionStorage.setItem("uploadedResumeUrl", uploadResult.data);
          sessionStorage.setItem("uploadedResumeName", file.name);
        }
      } catch (uploadError) {
        cogoToast.error("Failed to upload resume file", {
          hideAfter: 4,
          position: "top-center",
        });
      }

      // Parse the resume with AI (fallback to regex if AI fails)
      try {
        console.log("Attempting AI-based resume parsing...");
        const data = await parseResumeWithAI(file);
        console.log("AI parsed resume data:", data);
        console.log("About/Summary:", data.about);
        setParsedData(data);
        cogoToast.success("Resume parsed with AI successfully! 🤖✨", {
          hideAfter: 3,
          position: "top-center",
        });
      } catch (aiError) {
        console.warn(
          "AI parsing failed, falling back to regex parser:",
          aiError
        );

        // Fallback to regex parser
        try {
          const data = await parseResume(file);
          console.log("Regex parsed resume data:", data);
          console.log("About/Summary:", data.about);
          setParsedData(data);
          cogoToast.info("Resume parsed successfully (basic mode)", {
            hideAfter: 3,
            position: "top-center",
          });
        } catch (regexError) {
          cogoToast.error(regexError.message || "Failed to parse resume", {
            hideAfter: 4,
            position: "top-center",
          });
          setPreviewOpen(false);
        }
      } finally {
        setIsParsing(false);
        // Reset file input
        parseResumeInputRef.current.value = "";
      }
    }
  };

  const handleApplyParsedData = (data) => {
    // Apply extracted data to form fields
    if (data.first_name) {
      dispatch({
        type: EDIT_PROFILE,
        payload: { name: "first_name", value: data.first_name },
      });
    }
    if (data.last_name) {
      dispatch({
        type: EDIT_PROFILE,
        payload: { name: "last_name", value: data.last_name },
      });
    }
    if (data.phone) {
      // Validate phone before applying
      if (isValidE164PhoneNumber(data.phone)) {
        dispatch({
          type: EDIT_PROFILE,
          payload: { name: "phone", value: data.phone },
        });
      }
    }
    if (data.about) {
      dispatch({
        type: EDIT_PROFILE,
        payload: { name: "about", value: data.about },
      });
    }

    // Store full parsed data in session storage for other sections to use
    sessionStorage.setItem("parsedResumeData", JSON.stringify(data));

    // Get the uploaded resume URL and name from sessionStorage
    const uploadedResumeUrl = sessionStorage.getItem("uploadedResumeUrl");
    const uploadedResumeName = sessionStorage.getItem("uploadedResumeName");

    // Update the user's profile with the resume
    if (uploadedResumeUrl && uploadedResumeName) {
      const payload = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        gender: profileData.gender,
        dob: profileData.dob,
        state: profileData.state,
        country: profileData.country,
        industry: profileData.industry,
        image_url: profileData.image_url,
        resume: uploadedResumeUrl,
        resumeName: uploadedResumeName,
        phone: profileData.phone,
        links: profileData.links,
        isShowOnProfile: 1,
        about: data.about || profileData.about,
      };

      if (data.extracted_text) {
        payload.resume_extracted_text = data.extracted_text;
      }
      if (data.skills?.length) {
        payload.resume_extracted_skills = data.skills;
      }

      dispatch({ type: PROFILE_LOADING });

      updateProfileAction(payload, dispatch)
        .then(() => {
          cogoToast.success(
            "Resume and basic info applied to profile successfully!",
            {
              hideAfter: 6,
              position: "top-center",
            }
          );
        })
        .catch((error) => {
          cogoToast.error(
            error?.response?.data?.message || "Failed to update resume",
            {
              hideAfter: 4,
              position: "top-center",
            }
          );
        })
        .finally(() => {
          dispatch({ type: STOP_PROFILE_LOADING });
        });
    } else {
      cogoToast.success(
        "Basic info populated! Navigate to other sections (About, Education, Experience) to apply additional data.",
        {
          hideAfter: 6,
          position: "top-center",
        }
      );
    }

    setPreviewOpen(false);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setParsedData(null);
  };

  const handleSubmit = () => {
    // Validate phone number if it's provided
    if (profileData.phone && !isValidE164PhoneNumber(profileData.phone)) {
      cogoToast.error(
        "Please enter a valid phone number in E.164 format (e.g., +1234567890)",
        {
          hideAfter: 4,
          position: "top-center",
        }
      );
      return;
    }

    const originalData = JSON.parse(
      sessionStorage.getItem("userData") ||
        localStorage.getItem("userData") ||
        "{}"
    );

    const payload = {
      first_name: profileData.first_name,
      last_name: profileData.last_name,
      title: profileData.title,
      gender: profileData.gender,
      dob: profileData.dob,
      state: profileData.state,
      country: profileData.country,
      industry: profileData.industry,
      image_url: profileData.image_url,
      resume: profileData.resume,
      links: profileData.links,
      isShowOnProfile: 1,
      about: profileData.about,
      location: profileData.location || "",
    };

    // Include phone only if changed
    if (originalData.phone !== profileData.phone || !originalData.phone) {
      payload.phone = profileData.phone;
    }

    dispatch({ type: PROFILE_LOADING });

    updateProfileAction(payload, dispatch)
      .catch((error) => {
        // Extract a single error message
        const errorMessage =
          error?.payload ||
          error?.response?.data?.message ||
          "Error updating profile";

        if (
          errorMessage.includes("OTP") ||
          errorMessage.includes("verification")
        ) {
          cogoToast.error(
            "Phone verification required. Please verify your phone number first.",
            {
              hideAfter: 6,
              position: "top-center",
            }
          );
        } else {
          cogoToast.error(errorMessage, {
            hideAfter: 4,
            position: "top-center",
          });
        }
      })
      .finally(() => {
        dispatch({ type: STOP_PROFILE_LOADING });
      });
  };

  useEffect(() => {
    const storedTitle = localStorage.getItem("title");
    if (storedTitle && storedTitle !== profileData.title) {
      dispatch({
        type: EDIT_PROFILE,
        payload: { name: "title", value: storedTitle },
      });
    }
  }, [dispatch]);

  // Add useEffect to initialize phone verification status from profileData
  useEffect(() => {
    // You might want to check if phone is verified from profileData
    // setIsPhoneVerified(profileData.phone_verified || false);
  }, [profileData.phone]);

  return (
    <>
      <EditCard
        handleSubmit={handleSubmit}
        allRequired
        loading={profileLoading}
        btnText="Update"
        disable={[
          !profileData.first_name,
          !profileData.last_name,
          !profileData.title,
          !profileData.phone,
          !profileData.country,
          !profileData.state,
          !profileData.location,
          !profileData.dob,
          !profileData.gender,
          !!phoneError,
        ].includes(true)}
      >
        {/* Pull from Resume Section */}
        <div
          className="w-100 mb-4"
          style={{
            paddingBottom: "20px",
            borderBottom: "1px solid #e0e0e0",
            padding: '16px',
            marginLeft: '0',
            marginRight: '0',
            paddingLeft: '0',
            paddingRight: '0'
          }}
        >
          <h6 style={{ marginBottom: "8px", fontSize: '16px' }}>Quick Fill from Resume</h6>
          <p style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>
            Upload your resume to automatically populate your basic information
          </p>
          <input
            ref={parseResumeInputRef}
            type="file"
            onChange={handleResumeFileParse}
            style={{ display: "none" }}
            accept="application/pdf, .docx"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleParseResumeClick}
            disabled={isParsing}
            style={{
              backgroundColor: "#28a745",
              marginBottom: "8px",
              width: '100%',
              padding: '12px',
              fontSize: '14px'
            }}
          >
            {isParsing ? "Parsing..." : "📄 Auto-fill from Resume"}
          </Button>
        </div>

        {/* Form Fields */}
        <TextField
          onChange={handleChange}
          name="first_name"
          value={profileData.first_name || ""}
          className="w-100 mb-4"
          variant="outlined"
          label="First name"
          style={{ margin: '0 0 16px 0' }}
        />
        <TextField
          onChange={handleChange}
          name="last_name"
          value={profileData.last_name || ""}
          className="w-100 mb-4"
          variant="outlined"
          label="Last name"
          style={{ margin: '0 0 16px 0' }}
        />
        <TextField
          onChange={handleChange}
          name="title"
          value={profileData.title || ""}
          className="w-100 mb-4"
          variant="outlined"
          label="Title"
          style={{ margin: '0 0 16px 0' }}
        />
        <TextField
          name="email"
          disabled
          value={profileData.email || ""}
          className="w-100 mb-4"
          variant="outlined"
          label={ternaryResolver(profileData.email, "", "Email Address")}
          style={{ margin: '0 0 16px 0' }}
        />

        <FormControl
          variant="outlined"
          className={`w-100 mb-4 ${classes.inputField}`}
          style={{ margin: '0 0 16px 0' }}
        >
          <InputLabel name="country-label">Country</InputLabel>
          <Select
            labelId="country-label"
            name="country"
            label="country"
            value={profileData.country || ""}
            onChange={handleChange}
          >
            <MenuItem value="">Select a country</MenuItem>
            {countries.map(({ name, id }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          className={`w-100 mb-4 ${classes.inputField}`}
          style={{ margin: '0 0 16px 0' }}
        >
          <InputLabel name="state-label">State</InputLabel>
          <Select
            labelId="state-label"
            name="state"
            label="state"
            value={profileData.state || ""}
            onChange={handleChange}
            disabled={states.length === 0}
          >
            <MenuItem value="">Select state</MenuItem>
            {states.map(({ name, id }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isOnEditLocation ? (
          <FormControl
            variant="outlined"
            className={`w-100 mb-4 ${classes.inputField}`}
            style={{ margin: '0 0 16px 0' }}
          >
            <SearchLocationInput
              inputStyle="TextField"
              value={profileData.location || ""}
              handleLocationChange={handleLocationChange}
            />
          </FormControl>
        ) : (
          <>
            <div className={classes.locationChangeContainer}>
              <span
                onClick={() => setIsOnEditLocation(true)}
                className={classes.locationLink}
              >
                Change City
              </span>
            </div>
            <TextField
              onChange={handleChange}
              value={profileData.location || ""}
              name="City"
              className="w-100 mb-4"
              variant="outlined"
              label="City"
              style={{ margin: '0 0 16px 0' }}
            />
          </>
        )}
        {/* Phone Number with Verification Status */}
        <div className="w-100 mb-4" style={{ margin: '0 0 16px 0' }}>
          <div className={classes.phoneHeader}>
            <span style={{ fontSize: "14px", fontWeight: "bold" }}>
              Phone Number
            </span>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  backgroundColor:
                    profileData.is_phone_verified == 1 ? "#4caf50" : "#f44336",
                  color: "white",
                  marginLeft: isMobile ? "0px" : "10px",
                }}
              >
                {profileData.is_phone_verified == 1
                  ? "Verified"
                  : "Not Verified"}
              </span>
              {profileData.is_phone_verified == 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSendOtp}
                  disabled={
                    isSendingOtp ||
                    !profileData.phone ||
                    !profileData.country ||
                    !!phoneError ||
                    !isPhoneSavedAndMatching
                  }
                  style={{ marginLeft: "10px" }}
                >
                  {isSendingOtp ? "Sending..." : "Verify"}
                </Button>
              )}
            </div>
          </div>
          <TextField
            onChange={handleChange}
            value={profileData.phone || ""}
            name="phone"
            className="w-100"
            variant="outlined"
            label="Phone Number"
            error={!!phoneError}
            helperText={
              phoneError ||
              "Enter phone number in E.164 format (e.g., +1234567890)"
            }
            placeholder="+1234567890"
          />
        </div>

        <TextField
          InputLabelProps={{
            shrink: true,
          }}
          onChange={handleChange}
          value={profileData.dob || ""}
          name="dob"
          type="date"
          className="w-100 mb-4"
          variant="outlined"
          label="Date of birth"
          style={{ margin: '0 0 16px 0' }}
        />

        <FormControl
          variant="outlined"
          className={`w-100 mb-4 ${classes.inputField}`}
          style={{ margin: '0 0 16px 0' }}
        >
          <InputLabel name="gender-label">Gender</InputLabel>
          <Select
            labelId="gender-label"
            name="gender"
            label="gender"
            onChange={handleChange}
            value={profileData.gender || ""}
          >
            <MenuItem value="">Select a gender</MenuItem>
            <MenuItem value="2">Female</MenuItem>
            <MenuItem value="1">Male</MenuItem>
            <MenuItem value="3">Others</MenuItem>
          </Select>
        </FormControl>
      </EditCard>

      {/* OTP Verification Modal */}
      <Dialog
        open={otpModalOpen}
        onClose={() => !isVerifyingOtp && setOtpModalOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Verify Phone Number</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: "16px", wordBreak: "break-word" }}>
            Please enter the 6-digit OTP sent to your phone number:{" "}
            <b>{profileData.phone}</b>
          </p>
          <TextField
            autoFocus
            margin="dense"
            label="Enter OTP"
            type="text"
            fullWidth
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            inputProps={{
              maxLength: 6,
              className: classes.otpInput,
            }}
            InputProps={{
              style: {
                borderRadius: "12px",
              },
            }}
            variant="outlined"
            disabled={isVerifyingOtp}
            placeholder="123456"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOtpModalOpen(false)}
            disabled={isVerifyingOtp}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyOtp}
            color="primary"
            disabled={isVerifyingOtp || !otp || otp.length !== 6}
          >
            {isVerifyingOtp ? "Verifying..." : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Preview Modal */}
      <ResumePreviewModal
        open={previewOpen}
        handleClose={handleClosePreview}
        parsedData={parsedData}
        onApply={handleApplyParsedData}
        isLoading={isParsing}
      />

      {/* Phone Verification Required Modal */}
      <Dialog
        open={verificationRequiredDialog.open}
        onClose={() => setVerificationRequiredDialog({ open: false, pendingCountryValue: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ paddingBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: 48, 
              height: 48, 
              borderRadius: '50%', 
              backgroundColor: '#FFF3E0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24
            }}>
              🔒
            </div>
            <span style={{ fontSize: 20, fontWeight: 600, color: '#1f1f1f' }}>
              Phone Verification Required
            </span>
          </div>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          <div style={{ 
            padding: '16px 20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: 12,
            borderLeft: '4px solid #FF9800',
            marginBottom: 20
          }}>
            <p style={{ margin: 0, fontSize: 15, color: '#1f1f1f', lineHeight: 1.6 }}>
              You must verify your phone number before changing your country.
            </p>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <p style={{ 
              margin: '0 0 12px 0', 
              fontSize: 14, 
              fontWeight: 600, 
              color: '#1f1f1f' 
            }}>
              How to verify:
            </p>
            <ol style={{ 
              margin: 0, 
              paddingLeft: 20, 
              color: '#6c757d', 
              fontSize: 14,
              lineHeight: 1.8
            }}>
              <li>Scroll to the "Phone Number" field above</li>
              <li>Click the <strong style={{ color: '#3a6ed4' }}>"Verify"</strong> button</li>
              <li>Enter the OTP sent to your phone</li>
              <li>Return here to change your country</li>
            </ol>
          </div>

          {!profileData.phone && (
            <div style={{ 
              padding: '12px 16px', 
              backgroundColor: '#ffebee', 
              borderRadius: 8,
              border: '1px solid #ef5350',
              marginTop: 16
            }}>
              <p style={{ 
                margin: 0, 
                color: '#c62828', 
                fontSize: 13, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                You need to add and save a phone number first.
              </p>
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setVerificationRequiredDialog({ open: false, pendingCountryValue: null })}
            variant="contained"
            color="primary"
            style={{ 
              textTransform: 'none',
              padding: '8px 24px',
              borderRadius: 8,
              fontWeight: 600
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Country Code Mismatch Modal */}
      <Dialog
        open={countryCodeMismatchDialog.open}
        onClose={() =>
          setCountryCodeMismatchDialog({ open: false, targetCountry: null, targetCountryName: '', targetPhoneCode: '' })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ paddingBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: 48, 
              height: 48, 
              borderRadius: '50%', 
              backgroundColor: '#FFF3E0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24
            }}>
              📞
            </div>
            <span style={{ fontSize: 20, fontWeight: 600, color: '#1f1f1f' }}>
              Phone Number Mismatch
            </span>
          </div>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          <div style={{ 
            padding: '16px 20px', 
            backgroundColor: '#fff3e0', 
            borderRadius: 12,
            borderLeft: '4px solid #FF9800',
            marginBottom: 20
          }}>
            <p style={{ margin: 0, fontSize: 15, color: '#1f1f1f', lineHeight: 1.6 }}>
              Your phone number must match the country code for <strong>{countryCodeMismatchDialog.targetCountryName}</strong> (+{countryCodeMismatchDialog.targetPhoneCode}).
            </p>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <p style={{ 
              margin: '0 0 12px 0', 
              fontSize: 14, 
              fontWeight: 600, 
              color: '#1f1f1f' 
            }}>
              To change to {countryCodeMismatchDialog.targetCountryName}:
            </p>
            <ol style={{ 
              margin: 0, 
              paddingLeft: 20, 
              color: '#6c757d', 
              fontSize: 14,
              lineHeight: 1.8
            }}>
              <li>Update your phone number to start with <strong style={{ color: '#FF9800' }}>+{countryCodeMismatchDialog.targetPhoneCode}</strong></li>
              <li>Click <strong style={{ color: '#3a6ed4' }}>"Update"</strong> to save the new phone number</li>
              <li>Click <strong style={{ color: '#3a6ed4' }}>"Verify"</strong> to verify the new number</li>
              <li>Enter the OTP sent to your new phone</li>
              <li>Then you can change your country to {countryCodeMismatchDialog.targetCountryName}</li>
            </ol>
          </div>

          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#e3f2fd', 
            borderRadius: 8,
            border: '1px solid #2196f3',
            marginTop: 16
          }}>
            <p style={{ 
              margin: 0, 
              color: '#1565c0', 
              fontSize: 13, 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ fontSize: 16 }}>💡</span>
              Current phone: <strong>{profileData.phone || 'Not set'}</strong>
            </p>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setCountryCodeMismatchDialog({ open: false, targetCountry: null, targetCountryName: '', targetPhoneCode: '' })}
            variant="contained"
            color="primary"
            style={{ 
              textTransform: 'none',
              padding: '8px 24px',
              borderRadius: 8,
              fontWeight: 600
            }}
          >
            Understood
          </Button>
        </DialogActions>
      </Dialog>

      {/* Country Change Wallet Warning */}
      <Dialog
        open={countryChangeDialog.open}
        onClose={handleCancelCountryChange}
        maxWidth="sm"
      >
        <DialogTitle>Change Country?</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 8 }}>
            Changing your country will switch your active wallet from{" "}
            <strong>{getWalletCurrencyForCountry(profileData.country)}</strong> to{" "}
            <strong>{getWalletCurrencyForCountry(countryChangeDialog.pendingValue)}</strong>.
          </p>
          <p style={{ color: "#6c757d", fontSize: 14 }}>
            Your previous wallet balance will no longer be accessible from the
            Wallet page. Existing transactions and invoices are not affected.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCountryChange}>Cancel</Button>
          <Button onClick={handleConfirmCountryChange} color="primary">
            Confirm
          </Button>
        </DialogActions>
  </Dialog>
  </>);
};

BasicInfo.propTypes = {
  // industries: arrayPropType.isRequired,
  countries: arrayPropType.isRequired,
  states: arrayPropType.isRequired,
  profileData: objectPropType.isRequired,
  profileLoading: bool.isRequired,
};

export default BasicInfo;